import type {
  ClusterTop,
  DrillLocation,
  ExplorationVolume,
} from '../../types/exploration';
import { clusterTopForVolume, clusterTopKeyString } from '../drill/drillLocation';
import { reasonLabelForVolume } from '../sidebar/focusedReasonHelpers';
import { volumeStatusLabel } from '../sidebar/volumeStatusLabel';
import type { GroupingModel } from './types';
import {
  volumeAccountKey,
  volumePassedThroughInstance,
  withVolumeStatusOverlay,
} from './groupingPathShared';

/** Node group segment, or undefined when it's the redundant name-matches-cluster case. */
function nodeGroupSegment(volume: ExplorationVolume, top: ClusterTop): string | undefined {
  if (top.kind !== 'cluster') return undefined;
  const raw = volume.nodeGroup || '(No Group)';
  return raw !== top.name ? raw : undefined;
}

export const clusterGrouping: GroupingModel = {
  secondary: 'cluster',

  locationForVolume(volume, opts) {
    const account = opts.groupByAccount ? volumeAccountKey(volume) : undefined;
    const top = clusterTopForVolume(volume);

    // Unattached volumes have no instanceId, so volumePassedThroughInstance
    // below is always false for them — the ternary already resolves to
    // { kind: 'cluster', top } in that case, no special-case needed.
    const structural: DrillLocation['structural'] = volumePassedThroughInstance(
      opts.currentLocation,
      volume
    )
      ? {
          kind: 'cluster',
          top,
          nodeGroup: nodeGroupSegment(volume, top),
          instanceId: volume.instanceId!,
        }
      : { kind: 'cluster', top };

    return withVolumeStatusOverlay({ account, structural }, volume, opts.view);
  },

  locationForInstance(anchor, instanceId, opts) {
    if (!instanceId) return null;
    const account = opts.groupByAccount ? volumeAccountKey(anchor) : undefined;
    const top = clusterTopForVolume(anchor);
    return {
      account,
      structural: { kind: 'cluster', top, nodeGroup: nodeGroupSegment(anchor, top), instanceId },
    };
  },

  scopeForLocation(volumes, location, opts) {
    let vols = volumes;
    if (location.account) vols = vols.filter((v) => volumeAccountKey(v) === location.account);

    const s = location.structural;
    if (s.kind === 'cluster') {
      const topKey = clusterTopKeyString(s.top);
      vols = vols.filter((v) => clusterTopKeyString(clusterTopForVolume(v)) === topKey);
      if (s.nodeGroup) {
        vols = vols.filter((v) => (v.nodeGroup || '(No Group)') === s.nodeGroup);
      }
      if (s.instanceId) {
        vols = vols.filter((v) => (v.instanceId || '(no instance)') === s.instanceId);
      }
    }

    if (location.statusLabel) {
      vols = vols.filter((v) => volumeStatusLabel(v, opts.view) === location.statusLabel);
    }
    if (location.focusedActivationKey) {
      vols = vols.filter((v) => v.activationStatus === location.focusedActivationKey);
    }
    if (location.reasonLabel) {
      vols = location.focusedActivationKey
        ? vols.filter(
            (v) => reasonLabelForVolume(v, location.focusedActivationKey!) === location.reasonLabel
          )
        : vols.filter((v) => v.supportLabel === location.reasonLabel);
    }

    return vols;
  },
};

export function locationVolumeMatchesReason(
  location: Pick<DrillLocation, 'focusedActivationKey' | 'reasonLabel'>,
  volume: ExplorationVolume
): boolean {
  if (!location.reasonLabel) return true;
  return location.focusedActivationKey
    ? reasonLabelForVolume(volume, location.focusedActivationKey) === location.reasonLabel
    : volume.supportLabel === location.reasonLabel;
}
